import { Component, Inject, Injector, INJECTOR, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { Device } from '@capacitor/device';
import { Subscription } from 'rxjs';
import { isNanoFeatureSupported, NanoFeature } from '../../drive-version';
import { NanoService } from '../../nano/nano.service';
import { RoomWithData } from '../../server-services/query-records/room-records';
import { RoomService } from '../../server-services/room.service';
import { DeviceFile } from '../../services/blobsaver-adapter';
import { DialogService } from '../../services/dialog.service';
import {
  CollectionUploadConflictPolicy,
  CollectionUploadStatus,
  DownloadManagerService,
  UploadCollectionController,
} from '../../services/download-manager-service';
import { NativeAppService } from '../../services/native-app.service';
import {
  FilePickerDialogComponent,
  FilePickerDialogData,
  FilePickerDialogResult,
} from '../file-picker-dialog/file-picker-dialog.component';
import { NanoDialogBaseComponent } from '../nano-base-dialog';
import { DRIVE_MANAGED_FOLDER_NAME } from '../../consts/strings';

export type BackupDialogData = {
  pathToUpload?: string;
  roomId?: string;
};

export enum BackupDialogTabs {
  ROOM_SELECTOR,
  DEVICE_SCANNER,
  BACKUP_PROGRESS,
}

export enum ScanPhases {
  NOT_STARTED = 0,
  READ_NANO_INFO = 1,
  READ_DEVICE_FILES = 2,
  READ_DRIVE_FILES = 3,
  CHECK_UPLOAD_STATE = 4,
  DONE = 6,
}

export type ScanMetaInfo = {
  allFiles: {
    amount: number;
    size: number;
  };
  upload: {
    amount: number;
    size: number;
  };
};

export type OriginalFileNameInfo = {
  baseName: string;
  ext: string;
};

@Component({
  selector: 'app-backup-dialog',
  templateUrl: './backup-dialog.component.html',
  styleUrl: './backup-dialog.component.scss',
})
export class BackupDialogComponent
  extends NanoDialogBaseComponent<BackupDialogComponent>
  implements OnDestroy
{
  public activeTab: BackupDialogTabs = BackupDialogTabs.ROOM_SELECTOR;
  public BACKUP_DIALOG_TABS = BackupDialogTabs;
  public SCAN_PHASES = ScanPhases;
  public STATE = CollectionUploadStatus;

  /**
   * It can be only one backup, which is marked with the conflictPolicy == CollectionUploadConflictPolicy.BACKUP_LOGIC
   */
  public backup?: UploadCollectionController;
  public driveList: RoomWithData[] = [];
  public isAllActiveDriveInfoLoading: boolean = false;
  public selectedRoom?: RoomWithData;
  public isClosed = false;

  public scanPhaseTitles = [
    { phase: ScanPhases.READ_NANO_INFO, title: marker('Reach target ViveSec') },
    { phase: ScanPhases.READ_DEVICE_FILES, title: marker('Collect device files') },
    { phase: ScanPhases.READ_DRIVE_FILES, title: marker('Read drive files') },
    { phase: ScanPhases.CHECK_UPLOAD_STATE, title: marker('Find already uploaded files') },
  ];

  constructor(
    @Inject(INJECTOR) injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data: BackupDialogData,
    private downloadManagerService: DownloadManagerService,
    private roomService: RoomService,
    private dialogService: DialogService,
    private nativeAppService: NativeAppService,
    private nanoService: NanoService
  ) {
    super(injector);

    this.backup = Object.values(this.downloadManagerService.getAllCollectionUploads()).find(
      (c) => c.conflictPolicy == CollectionUploadConflictPolicy.BACKUP_LOGIC
    );

    if (this.backup) {
      this.data.roomId = this.backup.roomId;
      this.data.pathToUpload = this.backup.rootPath;

      this.subscribeOnBackupProgress();

      /*if (this.backup.state == CollectionUploadStatus.DONE) {
        this.activeTab = BackupDialogTabs.DONE_WINDOW;
      } else {
        this.activeTab = BackupDialogTabs.BACKUP_PROGRESS;
      }*/
      this.activeTab = BackupDialogTabs.BACKUP_PROGRESS;
    } else {
      this.activeTab = BackupDialogTabs.ROOM_SELECTOR;
    }

    if (this.data.roomId) {
      this.roomService
        .getRoom(this.data.roomId)
        .then((data) => {
          this.selectedRoom = data;
        })
        .catch((e) => {
          console.log('selected room is invalid', e);
          this.dialogService.openAlertDialog(
            marker('Invalid Room'),
            marker('Can not get info about the selected room: ') + this.data.roomId
          );
        });
    }

    /*if (!this.data.pathToUpload) {
      this.getDefaultUploadPath().then((path) => {
        this.data.pathToUpload = path;
      });
    }*/

    this.getDefaultUploadPath().then((path) => {
      this.data.pathToUpload = path;
    });

    this.updateDriveList();
  }

  ngOnDestroy(): void {
    this.isClosed = true;
  }

  public goToScanTab() {
    this.activeTab = this.BACKUP_DIALOG_TABS.DEVICE_SCANNER;
    this.scanDeviceFiles();
  }

  public onRoomSelect(room: RoomWithData) {
    this.selectedRoom = room;
    this.data.roomId = room.id;
  }

  private updateDriveList() {
    this.isAllActiveDriveInfoLoading = true;

    if (this.isClosed) return;
    this.roomService.getAllRoomWithActiveDrive().then((rooms) => {
      if (this.isClosed) return;
      let roomIdListWithActiveDrive = rooms.map((r) => r.id);
      this.roomService.getRooms(roomIdListWithActiveDrive).subscribe((rooms) => {
        this.driveList = rooms.sort((r1, r2) => {
          let name1 = r1.data?.name;
          let name2 = r2.data?.name;
          if (!name1 || name1 < name2) return -1;
          if (!name2 || name1 > name2) return 1;
          return 0;
        });
        this.isAllActiveDriveInfoLoading = false;
      });
    });
  }

  private async getDefaultUploadPath() {
    let deviceInfo = await Device.getInfo();
    let deviceId = await Device.getId();
    return (
      DRIVE_MANAGED_FOLDER_NAME +
      '/Gallery/' +
      (deviceInfo.name || deviceInfo.model) +
      ' ' +
      deviceId.identifier
    );
  }

  public close() {
    this.dialogRef.close();
  }

  public browsePathToUpload() {
    let data: FilePickerDialogData = {
      roomId: this.selectedRoom.id,
      multiSelection: false,
      title: marker('Browse Backup Destination Folder'),
      onlyDirectory: true,
    };
    const dialogRef = this.dialog.open(FilePickerDialogComponent, {
      data,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result: FilePickerDialogResult) => {
      if (!result || result.files.length == 0) return;

      this.data.pathToUpload = result.files[0]?.rawData['path'] || '';
    });
    return;
  }

  public scanFiles = false;
  public scanErrors = [];
  public scanPhase: ScanPhases = ScanPhases.NOT_STARTED;
  public filesToUpload: DeviceFile[] = [];
  public scanMetaInfo?: ScanMetaInfo;

  private resetScan() {
    this.scanErrors = [];
    this.filesToUpload = [];
    this.scanMetaInfo = undefined;
    this.scanFiles = false;
    this.scanPhase = ScanPhases.NOT_STARTED;
  }

  public async scanDeviceFiles(alreadyScannedFiles?: DeviceFile[]) {
    try {
      this.resetScan();

      let roomId = this.data.roomId;
      let pathToUpload = this.data.pathToUpload || '';
      this.scanFiles = true;

      // read nano version ---------------------------------------------------------------
      if (this.isClosed) return;
      this.scanPhase = ScanPhases.READ_NANO_INFO;
      let nanoInfo;
      try {
        nanoInfo = await this.roomService.getNanoSession(roomId);

        if (!isNanoFeatureSupported(nanoInfo.version, NanoFeature.BACKUP)) {
          console.error('nano version error', nanoInfo);
          this.scanErrors.push(
            marker(
              'Backup is not supported on this drive. The minimum required version for backup is v1.7.0.'
            )
          );
          this.scanFiles = false;
          return;
        }
      } catch (e) {
        console.error(e);
        this.scanErrors.push(marker('Could not read the target ViveSec info'));
        this.scanFiles = false;
        return;
      }

      // get device files from device ----------------------------------------------------
      if (this.isClosed) return;
      this.scanPhase = ScanPhases.READ_DEVICE_FILES;
      let deviceFiles: DeviceFile[];
      try {
        deviceFiles = alreadyScannedFiles || (await this.nativeAppService.getDeviceFiles());
      } catch (e) {
        console.error(e);
        this.scanErrors.push(marker('Could not read device files'));
        this.scanFiles = false;
        return;
      }

      if (!deviceFiles) {
        this.scanErrors.push(
          marker(
            'Permission Error. Please grant ViveSec the "Photos and Videos" permission in your phone settings and try again.'
          )
        );
        this.scanFiles = false;
        return;
      }

      // read root/subfolders -----------------------------------------------------
      this.scanPhase = ScanPhases.READ_DRIVE_FILES;

      let deviceFilesFolderMap = this.createFilesByFolderMapUsingDeviceFiles(
        pathToUpload,
        deviceFiles
      );

      let driveFiles: { [folderPath: string]: { name; mtime; size }[] } = {};

      for (let folder of Object.keys(deviceFilesFolderMap)) {
        if (this.isClosed) return;
        try {
          driveFiles[folder] = await new Promise((resolve, reject) => {
            this.nanoService.listDir(
              roomId,
              folder,
              nanoInfo.version,
              (result) => {
                resolve(result);
              },
              (err) => {
                reject(err);
              }
            );
          });
        } catch (e) {
          driveFiles[folder] = [];
        }
      }

      // check upload state, compare device and drive files -------------------
      if (this.isClosed) return;
      this.scanPhase = ScanPhases.CHECK_UPLOAD_STATE;
      let shouldCheckMTime = !this.nativeAppService.isOnIOS();

      deviceFiles.forEach((f) => {
        let folder = this.createPath(pathToUpload, f.relativeFolderPath);

        let originalFileNameInfo = this.getFileOriginalNameInfo(f.name);
        if (
          !this.isFileAlreadyExist(f, originalFileNameInfo, driveFiles[folder], shouldCheckMTime)
        ) {
          this.filesToUpload.push(f);
          f.aliasName = this.createAlias(
            f,
            originalFileNameInfo,
            deviceFilesFolderMap[folder],
            driveFiles[folder]
          );
        }
      });

      this.scanMetaInfo = {
        allFiles: {
          size: deviceFiles.reduce((sum, file) => sum + file.size, 0),
          amount: deviceFiles.length,
        },
        upload: {
          size: this.filesToUpload.reduce((sum, file) => sum + file.size, 0),
          amount: this.filesToUpload.length,
        },
      };

      //  done
      this.scanPhase = ScanPhases.DONE;
      this.scanFiles = false;
    } catch (e) {
      console.error(e);
      this.scanErrors.push(marker(`Unknown error happened during the process, check the console.`));
      this.scanFiles = false;
      return;
    }
  }

  private createPath(rootPath?, relativePath?, filename?) {
    let path = '';
    if (rootPath) path = rootPath;
    if (relativePath) {
      if (path) {
        path += '/' + relativePath;
      } else {
        path = relativePath;
      }
    }
    if (filename) {
      if (path) {
        path += '/' + filename;
      } else {
        path = filename;
      }
    }
    return path;
  }

  private createFilesByFolderMapUsingDeviceFiles(pathToUpload: string, deviceFiles: DeviceFile[]) {
    let deviceFilesFolderMap: { [folder: string]: DeviceFile[] } = {};
    deviceFiles.forEach((f) => {
      let folder = this.createPath(pathToUpload, f.relativeFolderPath);

      if (!deviceFilesFolderMap[folder]) {
        deviceFilesFolderMap[folder] = [f];
      } else {
        deviceFilesFolderMap[folder].push(f);
      }
    });
    return deviceFilesFolderMap;
  }

  private getFileOriginalNameInfo(name: string): OriginalFileNameInfo {
    const match = name.match(/^(.*?)(\.([^.]+))?$/);
    if (!match) return { baseName: '', ext: '' };

    const baseName = match[1];
    const ext = match[2] || '';

    return { baseName, ext };
  }

  /**
   * Create a unique name which is not occupied, and follow the "name (x).ext" format
   */
  private createAlias(
    file: DeviceFile,
    originalFileNameInfo: OriginalFileNameInfo,
    deviceFilesInTheSameFolder: DeviceFile[],
    driveFilesInTheSameFolder: { name; size; mtime }[]
  ): string {
    let iteration = 0;
    let name: string;

    do {
      if (iteration == 0) {
        name = originalFileNameInfo.baseName + originalFileNameInfo.ext;
      } else {
        name = `${originalFileNameInfo.baseName} (${iteration})${originalFileNameInfo.ext}`;
      }

      const isNameTakenInDeviceFiles = deviceFilesInTheSameFolder.some((f) => {
        if (f === file) return false; // skip the file itself
        return f.aliasName === name || f.name === name;
      });

      const isNameTakenInDriveFiles = driveFilesInTheSameFolder.some((f) => f.name === name);

      if (!isNameTakenInDeviceFiles && !isNameTakenInDriveFiles) {
        // not found, so you can take it as alias name
        return name;
      }

      iteration++;
    } while (true);
  }

  private isFileAlreadyExist(
    file: DeviceFile,
    originalFileNameInfo: OriginalFileNameInfo,
    filesInTheSameFolder: { name; size; mtime }[],
    checkMTime = true
  ) {
    let conflictedByName = this.getFilesWithSimilarName(originalFileNameInfo, filesInTheSameFolder);
    for (let i = 0; i < conflictedByName.length; i++) {
      if (this.isSameFile(file, conflictedByName[i], checkMTime)) {
        return true;
      }
    }
    return false;
  }

  private isSameFile(
    f: DeviceFile,
    fileWithSameNameOnDrive: { name; size; mtime },
    checkMTime = true
  ) {
    return (
      f.size == fileWithSameNameOnDrive.size &&
      (!checkMTime || this.isSameMTime(f.mTime, f.mtFrac, fileWithSameNameOnDrive.mtime))
    );
  }

  /**
   * File name can has special chataters, which can mess up o ur regular expression
   * when looking for similar files
   * @param str
   * @returns
   */
  private escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape all regexp spec characters
  }

  /**
   * Get the files which have chance to be the same file on the drive
   * @param name name of the file
   * @param fileList drive files
   * @returns
   */
  private getFilesWithSimilarName(
    originalFileNameInfo: OriginalFileNameInfo,
    fileList: { name; size; mtime }[]
  ) {
    const escapedBaseName = this.escapeRegExp(originalFileNameInfo.baseName);
    const escapedExt = this.escapeRegExp(originalFileNameInfo.ext);

    const regex = new RegExp(`^${escapedBaseName}( \\(\\d+\\))?${escapedExt}$`);

    return fileList.filter((f) => regex.test(f.name));
  }

  private backupSubscription: Subscription;
  public currFileProgress = 0;
  public async startBackup() {
    this.scanPhase = ScanPhases.NOT_STARTED;
    this.scanErrors = [];
    this.scanMetaInfo = undefined;
    this.activeTab = BackupDialogTabs.BACKUP_PROGRESS;
    if (!this.backup) {
      this.backup = await this.downloadManagerService.startCollectionUpload(
        this.data.roomId,
        this.data.pathToUpload,
        this.filesToUpload,
        CollectionUploadConflictPolicy.BACKUP_LOGIC
      );
      this.subscribeOnBackupProgress();
    }
  }

  public pause() {
    this.backup?.pause();
  }

  public resume() {
    this.backup?.resume();
  }

  public async delete() {
    this.backupSubscription?.unsubscribe();
    this.backup?.clear();
    this.backup = undefined;
  }

  private subscribeOnBackupProgress() {
    this.backupSubscription = this.backup.subject.subscribe({
      next: (value) => {
        this.currFileProgress = value.progress;
      },
      complete: async () => {
        this.backupSubscription?.unsubscribe();
        this.backup?.clear();
        this.scanDeviceFiles(this.filesToUpload);
      },
    });
  }

  public resetStates() {
    this.delete();
    this.resetScan();
  }

  /**
   * checking difference between two dates. The difference should be less than 0.000001 sec
   * @param mTimeInMS checking file date on the device (ios, or android) in ms
   * @param mtFracStartsWithOne the fraction parts, starting with 1: 1696071920.4031038 -> 14031038
   * @param checkedFileMTimeInSec the file date on the server in sec
   * @returns
   */
  private isSameMTime(
    mTimeInMS: number,
    mtFracStartsWithOne: number,
    checkedFileMTimeInSec: number
  ): boolean {
    var mtFrac = (mtFracStartsWithOne + '').slice(1); // remove the frist digit
    var mTimeInSec = Math.floor(mTimeInMS / 1000); // we need only the seconds
    var mTime = parseFloat(mTimeInSec + '.' + mtFrac); // concat the seconds and the fraction
    return Math.abs(mTime - checkedFileMTimeInSec) < 0.000001; // check the difference with this precision
  }
}
